import Call from "../../services/Call";

/** A call older than this without an end is a lost hangup, not a live one. */
const MAX_LIVE_CALL_MS = 3 * 60 * 60 * 1000;

const OUTGOING_TYPES: Call.Model["type"][] = [
	"OUT_COME",
	"OUT_ANSWER",
	"OUT_NO_ANSWER",
	"NEW_CALL",
];

export type CallDirection = "incoming" | "outgoing";

export type CallStatus = "accepted" | "rejected" | "missed";

export const isOutgoing = (call: Call.Model) =>
	OUTGOING_TYPES.includes(call.type);

export const getDirection = (call: Call.Model): CallDirection =>
	isOutgoing(call) ? "outgoing" : "incoming";

const toTime = (value?: string | Date | null): number | null => {
	if (!value) return null;
	const time = new Date(value).getTime();
	return Number.isFinite(time) ? time : null;
};

/**
 * The asterisk unique id starts with the unix time of the call, which is the
 * fallback for rows that were stored without a start date.
 */
export const getStartTime = (call: Call.Model): number | null => {
	const start = toTime(call.startDate);
	if (start) return start;

	const unix = Number(call.asteriskUniqueId?.split(".")[0]);
	if (Number.isFinite(unix) && unix > 0) return unix * 1000;

	return toTime(call.createdAt);
};

export const getAnswerTime = (call: Call.Model) => toTime(call.answerDate);

/** A call is live until the PBX reports how it ended. */
export const isLive = (call: Call.Model, now: number): boolean => {
	if (call.endDate || call.dialStatus) return false;
	if (!["IN_COME", "OUT_COME", "NEW_CALL"].includes(call.type)) return false;

	const start = getStartTime(call);
	if (!start) return false;

	return now - start < MAX_LIVE_CALL_MS;
};

/**
 * How the call ended for the dispatcher. A live call is accepted once it is
 * answered and has no status while it is still ringing.
 */
export const getStatus = (call: Call.Model, now: number): CallStatus | null => {
	const notAnswered = isOutgoing(call) ? "rejected" : "missed";

	if (isLive(call, now)) return getAnswerTime(call) ? "accepted" : null;

	if (call.dialStatus === 1) return "accepted";
	if (call.dialStatus) return notAnswered;

	if (call.type === "IN_ANSWER" || call.type === "OUT_ANSWER")
		return "accepted";
	if (
		call.type === "IN_NO_ANSWER" ||
		call.type === "IN_RECALLED" ||
		call.type === "OUT_NO_ANSWER"
	)
		return notAnswered;

	return null;
};

const seconds = (value?: number | null) =>
	typeof value === "number" && Number.isFinite(value) && value > 0
		? Math.round(value)
		: 0;

/** Waiting and talking time in seconds, counted live while the call lasts. */
export const getTimings = (
	call: Call.Model,
	now: number,
): { wait: number; talk: number } => {
	const start = getStartTime(call);
	const answer = getAnswerTime(call);

	if (isLive(call, now)) {
		if (answer) {
			return {
				wait: start ? seconds((answer - start) / 1000) : 0,
				talk: seconds((now - answer) / 1000),
			};
		}
		return { wait: start ? seconds((now - start) / 1000) : 0, talk: 0 };
	}

	const wait = seconds(call.waitTimeInSeconds);
	const talk =
		typeof call.talkTimeInSeconds === "number"
			? seconds(call.talkTimeInSeconds)
			: seconds((call.durationInSeconds || 0) - wait);

	return { wait, talk };
};

const pad = (value: number) => String(value).padStart(2, "0");

/** 0:07, 12:34 or 1:02:03 - independent of locale and timezone. */
export const formatDuration = (totalSeconds: number): string => {
	const total = seconds(totalSeconds);
	const h = Math.floor(total / 3600);
	const m = Math.floor((total % 3600) / 60);
	const s = total % 60;

	return h ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
};

export const formatStart = (time: number | null): string => {
	if (!time) return "";
	const date = new Date(time);
	const clock = `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
		date.getSeconds(),
	)}`;

	if (date.toDateString() === new Date().toDateString()) return clock;

	return `${pad(date.getDate())}.${pad(
		date.getMonth() + 1,
	)}.${date.getFullYear()} ${clock}`;
};

interface RawPerson {
	name?: string;
	surname?: string;
	fatherName?: string;
}

/** Relations come as the server projects them, not as the client models. */
interface RawParties {
	customer?: { person?: RawPerson } | null;
	employee?: { customer?: { person?: RawPerson } } | null;
	executor?: { callSign?: string; person?: RawPerson } | null;
}

const joinName = (person?: RawPerson) =>
	[person?.surname, person?.name, person?.fatherName]
		.filter(Boolean)
		.join(" ");

/** Whom the dispatcher talked to. */
export const getPartyName = (call: Call.Model): string => {
	const { customer, employee, executor } = call as unknown as RawParties;

	if (customer) return joinName(customer.person);
	if (employee) return joinName(employee.customer?.person);
	if (executor) {
		return [executor.callSign, joinName(executor.person)]
			.filter(Boolean)
			.join(" · ");
	}

	return "";
};

export const getDispatcherName = (call: Call.Model): string =>
	joinName(call.dispatcherToSip?.dispatcher?.person);
